import { useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getApiBaseUrl } from "@/lib/config";
import { SESSION_EXPIRED_MESSAGE } from "@/lib/authApi";
import type { Boutique } from "@/components/catalog/EnableBoutiquesModal";

interface ApiError extends Error {
  response?: { data: unknown; status: number };
}

interface Product {
  id: number;
  name: string;
  base_price: string;
  category?: { id: number; name: string };
  custom?: boolean;
}

interface Category {
  id: number;
  name: string;
  parent?: number | null;
}

interface ProductBoutiques {
  locations: Array<Boutique>;
  total: number;
}
class CatalogApiService {
  private getAccessToken: () => Promise<string | null>;

  constructor(getAccessToken: () => Promise<string | null>) {
    this.getAccessToken = getAccessToken;
  }

  /**
   * Authenticated fetch against the API root. JSON bodies get a JSON content
   * type; FormData bodies let the browser set the multipart boundary.
   */
  private async request(
    path: string,
    options: RequestInit = {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    const baseUrl = getApiBaseUrl();
    const token = await this.getAccessToken();

    if (!token) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string>),
    };

    const response = await fetch(`${baseUrl}${path}`, { ...options, headers });

    if (!response.ok) {
      const errorBody = await response
        .json()
        .catch(() => ({ error: `API request failed: ${response.statusText}` }));
      const error = new Error("Erreur API");
      (error as ApiError).response = {
        data: errorBody,
        status: response.status,
      };
      throw error;
    }
    if (response.status === 204) {
      return null;
    }
    return response.json();
  }

  /** `request` scoped to the `/catalog` prefix. */
  private makeRequest(
    endpoint: string,
    options: RequestInit = {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    return this.request(`/catalog${endpoint}`, options);
  }

  async fetchProducts(): Promise<Product[]> {
    return this.makeRequest("/products/");
  }

  async fetchProductDetails(id: number): Promise<Product> {
    return this.makeRequest(`/products/${id}/`);
  }

  async createProduct(data: FormData): Promise<Product> {
    const productData: Record<string, unknown> = {};
    const imageFiles: File[] = [];

    // Separate image files from other product data
    data.forEach((value, key) => {
      if (key === "files") {
        if (value instanceof File) {
          imageFiles.push(value);
        }
      } else {
        // Don't add empty category to productData
        if (key === "category" && !value) {
          return;
        }
        productData[key] = value;
      }
    });

    // Step 1: Create the product with its data
    const newProduct = await this.makeRequest("/products/", {
      method: "POST",
      body: JSON.stringify(productData),
    });

    // Step 2: If there are images, upload them and link to the new product
    if (imageFiles.length > 0 && newProduct.id) {
      const imageFormData = new FormData();
      imageFiles.forEach((file) => {
        imageFormData.append("files", file);
      });
      imageFormData.append("product_id", newProduct.id.toString());
      await this.uploadImages(imageFormData);
    }

    return this.fetchProductDetails(newProduct.id); // Return product with images
  }

  async updateProduct(id: number, data: FormData): Promise<Product> {
    const productData: Record<string, unknown> = {};
    const imageFiles: File[] = [];

    // Separate image files from other product data
    data.forEach((value, key) => {
      if (key === "files") {
        if (value instanceof File) {
          imageFiles.push(value);
        }
      } else {
        if (key === "category" && !value) {
          productData[key] = null;
        } else {
          productData[key] = value;
        }
      }
    });

    // Step 1: Update the product with its data
    const updatedProduct = await this.makeRequest(`/products/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(productData),
    });

    // Step 2: If there are new images, upload them and link to the product
    if (imageFiles.length > 0 && updatedProduct.id) {
      const imageFormData = new FormData();
      imageFiles.forEach((file) => {
        imageFormData.append("files", file);
      });
      imageFormData.append("product_id", updatedProduct.id.toString());
      await this.uploadImages(imageFormData);
    }

    return this.fetchProductDetails(updatedProduct.id); // Return product with images
  }

  async deleteProduct(id: number): Promise<void> {
    await this.makeRequest(`/products/${id}/`, { method: "DELETE" });
  }

  async deleteProductImage(id: number): Promise<void> {
    await this.makeRequest(`/product-images/${id}/`, { method: "DELETE" });
  }

  async uploadImages(
    formData: FormData,
  ): Promise<{ uploaded_images: { id: number }[] }> {
    // Lives outside the /catalog prefix.
    return this.request("/images/bulk_upload/", {
      method: "POST",
      body: formData,
    });
  }

  // Similar for categories, variants, overrides
  async fetchCategories(): Promise<Category[]> {
    return this.makeRequest("/categories/");
  }

  async createCategory(data: {
    name: string;
    parent?: string;
  }): Promise<Category> {
    return this.makeRequest("/categories/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCategory(
    id: number,
    data: { name: string; parent?: string },
  ): Promise<Category> {
    return this.makeRequest(`/categories/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: number): Promise<void> {
    await this.makeRequest(`/categories/${id}/`, { method: "DELETE" });
  }

  async fetchProductBoutiques(
    productId: number,
    search: string,
  ): Promise<ProductBoutiques> {
    return this.makeRequest(
      `/products/${productId}/locations/?search=${search}`,
    );
  }

  async updateProductBoutique(
    productId: number,
    boutique: { location_id: number; enable: boolean },
  ): Promise<{ success: boolean }> {
    return this.makeRequest(`/products/${productId}/update/`, {
      method: "PATCH",
      body: JSON.stringify(boutique),
    });
  }
}

function useCatalogApiService() {
  const { getAccessToken } = useAuth();
  return useMemo(() => new CatalogApiService(getAccessToken), [getAccessToken]);
}

export function useCatalogApi() {
  const service = useCatalogApiService();

  const fetchProducts = useCallback(async () => {
    return service.fetchProducts();
  }, [service]);

  const fetchProductDetails = useCallback(
    async (id: number) => {
      return service.fetchProductDetails(id);
    },
    [service],
  );

  const createProduct = useCallback(
    async (data: FormData) => {
      return service.createProduct(data);
    },
    [service],
  );

  const updateProduct = useCallback(
    async (id: number, data: FormData) => {
      return service.updateProduct(id, data);
    },
    [service],
  );

  const deleteProduct = useCallback(
    async (id: number) => {
      return service.deleteProduct(id);
    },
    [service],
  );

  const deleteProductImage = useCallback(
    async (id: number) => {
      return service.deleteProductImage(id);
    },
    [service],
  );

  const uploadImages = useCallback(
    async (formData: FormData) => {
      return service.uploadImages(formData);
    },
    [service],
  );

  const fetchCategories = useCallback(async () => {
    return service.fetchCategories();
  }, [service]);

  const createCategory = useCallback(
    async (data: { name: string; parent?: string }) => {
      return service.createCategory(data);
    },
    [service],
  );

  const updateCategory = useCallback(
    async (id: number, data: { name: string; parent?: string }) => {
      return service.updateCategory(id, data);
    },
    [service],
  );

  const deleteCategory = useCallback(
    async (id: number) => {
      return service.deleteCategory(id);
    },
    [service],
  );

  const fetchProductBoutiques = useCallback(
    async (productId: number, search: string) => {
      return service.fetchProductBoutiques(productId, search);
    },
    [service],
  );
  const updateProductBoutique = useCallback(
    async (
      productId: number,
      boutique: { location_id: number; enable: boolean },
    ) => {
      return service.updateProductBoutique(productId, boutique);
    },
    [service],
  );

  return {
    fetchProducts,
    fetchProductDetails,
    createProduct,
    updateProduct,
    deleteProduct,
    deleteProductImage,
    uploadImages,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    fetchProductBoutiques,
    updateProductBoutique,
  };
}
